import { lazy, ComponentType } from 'react';

/**
 * A wrapper around React.lazy that retries failed dynamic imports.
 *
 * This handles the common "Failed to fetch dynamically imported module" error
 * that occurs when:
 * 1. A deployment happens while user has the app open (stale chunk hashes)
 * 2. Network issues cause temporary failures
 * 3. Ad blockers or browser extensions interfere
 *
 * On first failure, it will retry once. If that fails, it reloads the page
 * to get the latest chunk manifest.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const sessionKey = `chunk-reload-${importFn.toString().slice(0, 100)}`;
    const hasReloaded = sessionStorage.getItem(sessionKey);

    try {
      // First attempt
      return await importFn();
    } catch (error) {
      // Check if this is a chunk loading error
      const isChunkError = error instanceof Error && (
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Loading CSS chunk') ||
        error.name === 'ChunkLoadError'
      );

      if (!isChunkError) {
        // Not a chunk error, rethrow
        throw error;
      }

      // If we haven't reloaded yet, try reloading the page
      if (!hasReloaded) {
        sessionStorage.setItem(sessionKey, 'true');
        window.location.reload();
        // Return a never-resolving promise to prevent rendering stale content
        return new Promise(() => {});
      }

      // We've already reloaded once and it still failed
      // Clear the flag so future visits can try again
      sessionStorage.removeItem(sessionKey);
      throw error;
    }
  });
}
